import api from './api';
import { StatusOSC } from '../types/statusOSC.types';

const cache: Record<string, { id?: string; razaoSocial?: string; nomeFantasia?: string }> = {};

export interface InstitutionDTO {
  id?: string;
  institutionId: string;

  // Dados Básicos
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal: string;
  dataFundacao?: string; // yyyy-MM-dd
  areasAtuacao?: string[];

  // Contato
  telefone: string;
  celular?: string;
  emailInstitucional: string;
  emailSecundario?: string;
  website?: string;

  // Endereço
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
  pontoReferencia?: string;

  // Adicionais
  numeroRegistroConselhoMunicipal: string;
  dataRegistroConselho?: string; // yyyy-MM-dd
  objetoSocial?: string;
  quantidadeBeneficiarios?: number;

  // RF-02.3 - Status da OSC
  statusOSC?: StatusOSC;
  justificativaSuspensao?: string;

  // Bloqueio por prestação de contas pendente (independente do statusOSC)
  bloqueada?: boolean;
  motivoBloqueio?: string;
  bloqueadaEm?: string;
  bloqueadaPor?: string;

  // Status de Aprovação da Instituição
  statusAprovacao?: 'PENDENTE' | 'AGUARDANDO_ANALISE' | 'EM_ANALISE' | 'APROVADA' | 'REPROVADA';
  dataAprovacao?: string;
  dataReprovacao?: string;
  observacoesAprovacao?: string;
  motivoReprovacao?: string;
  usuarioAprovador?: string;

  // Auditoria
  dataCriacao?: string;
  dataAtualizacao?: string;
}

const institutionService = {
  // Lista TODAS as instituições (admin only / uso interno)
  list: async (): Promise<InstitutionDTO[]> => {
    const response = await api.get('/institutions');
    return response.data;
  },

  // Lista apenas instituições visíveis para o usuário logado (filtro por role no backend)
  // Accept optional axios config (e.g., { timeout: 5000 })
  listForUser: async (config?: any): Promise<InstitutionDTO[]> => {
    const response = await api.get('/institutions/for-user', config || {});
    return response.data;
  },

  getById: async (id: string): Promise<InstitutionDTO> => {
    // Backend uses institutionId as the identifier (@Id).
    const response = await api.get(`/institutions/by-institution-id/${encodeURIComponent(id)}`);
    return response.data;
  },

  getByInstitutionId: async (institutionId: string): Promise<InstitutionDTO> => {
    const response = await api.get(`/institutions/by-institution-id/${institutionId}`);
    return response.data;
  },

  getByCnpj: async (cnpj: string): Promise<InstitutionDTO> => {
    const response = await api.get(`/institutions/by-cnpj/${cnpj}`);
    return response.data;
  },

  create: async (institution: Partial<InstitutionDTO>): Promise<InstitutionDTO> => {
    console.log('📤 institutionService.create CHAMADO');
    console.log('⚠️ ATENÇÃO: Criando NOVA instituição (POST)');
    console.log('URL: /institutions');
    console.log('Payload:', institution);
    console.log('Método HTTP: POST');

    const response = await api.post('/institutions', institution);

    console.log('✅ institutionService.create SUCESSO');
    console.log('Response:', response.data);

    return response.data;
  },

  update: async (id: string, institution: Partial<InstitutionDTO>): Promise<InstitutionDTO> => {
    // Backend uses institutionId as the identifier (@Id).
    console.log('📤 institutionService.update CHAMADO');
    console.log('ID:', id);
    console.log('URL completa:', `/institutions/${encodeURIComponent(id)}`);
    console.log('Payload:', institution);
    console.log('Método HTTP: PUT');

    const response = await api.put(`/institutions/${encodeURIComponent(id)}`, institution);

    console.log('✅ institutionService.update SUCESSO');
    console.log('Response:', response.data);

    return response.data;
  },

  // RF-02.3 - Gerenciamento de Status
  alterarStatus: async (id: string, novoStatus: StatusOSC, justificativa?: string): Promise<InstitutionDTO> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/status`, {
      novoStatus,
      justificativa,
    });
    return response.data;
  },

  // Bloquear instituição (ex.: prestação de contas pendente)
  bloquear: async (id: string, motivo: string): Promise<InstitutionDTO> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/bloqueio`, { motivo });
    return response.data;
  },

  // Desbloquear instituição
  desbloquear: async (id: string): Promise<InstitutionDTO> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/desbloqueio`);
    return response.data;
  },

  calcularStatusAutomatico: async (id: string): Promise<{ statusCalculado: StatusOSC; mudou: boolean }> => {
    const response = await api.get(`/institutions/${encodeURIComponent(id)}/calcular-status`);
    return response.data;
  },

  getHistoricoStatus: async (id: string): Promise<any[]> => {
    const response = await api.get(`/institutions/${encodeURIComponent(id)}/historico-status`);
    return response.data;
  },

  // Marca declarações como concluídas para a instituição (Task-29)
  concluirDeclaracoes: async (id: string): Promise<{
    id?: string;
    instituicaoId?: string;
    tipo?: string;
    dados?: any;
    usuario?: string;
    usuarioId?: string;
    dataCriacao?: string;
  }> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/acoes/concluir-declaracoes`);
    return response.data;
  },

  // Vincular usuário logado à instituição
  vincularUsuario: async (id: string): Promise<{ message: string; usuario: string; instituicao: string }> => {
    console.log('🔗 institutionService.vincularUsuario CHAMADO');
    console.log('   Institution ID:', id);
    console.log('   URL:', `/institutions/${encodeURIComponent(id)}/vincular`);
    console.log('   Método: POST');

    const response = await api.post(`/institutions/${encodeURIComponent(id)}/vincular`);

    console.log('✅ institutionService.vincularUsuario SUCESSO');
    console.log('   Response:', response.data);

    return response.data;
  },

  // Move institution to the end of the Convênios queue (best-effort - backend may implement)
  moveToEndQueue: async (id: string): Promise<void> => {
    try {
      await api.post(`/institutions/${encodeURIComponent(id)}/fila/move-to-end`);
    } catch (err) {
      // Graceful: log and rethrow so callers can show feedback if desired
      console.warn('[institutionService] moveToEndQueue failed (endpoint may not exist):', err);
      throw err;
    }
  },

  // Fetch queue of institutions that are under analysis (for Convenios users)
  // Accepts optional axios config (e.g., { timeout: 5000 }) and an options object:
  // options.swallowErrors (default: true) — when true, network/server errors are
  // logged and an empty array is returned so the UI can gracefully fallback.
  getQueue: async (config?: any, options?: { swallowErrors?: boolean; retries?: number }): Promise<InstitutionDTO[]> => {
    const swallow = options?.swallowErrors ?? true;
    const maxRetries = typeof options?.retries === 'number' ? Math.max(0, options!.retries) : 2;
    try {
      let attempt = 0;
      let lastErr: any = null;
      while (attempt <= maxRetries) {
        try {
          const response = await api.get('/institutions/queue', config || {});
          const payload = response.data;

          // Normalize possible shapes
          if (Array.isArray(payload)) {
            console.debug('[institutionService.getQueue] received array, length=', payload.length);
            return payload as InstitutionDTO[];
          }

          if (!payload) {
            console.warn('[institutionService.getQueue] empty payload received from /institutions/queue');
            return [];
          }

          if (Array.isArray(payload.data)) {
            console.debug('[institutionService.getQueue] returned payload.data array, length=', payload.data.length);
            return payload.data as InstitutionDTO[];
          }
          if (Array.isArray(payload.content)) {
            console.debug('[institutionService.getQueue] returned payload.content array, length=', payload.content.length);
            return payload.content as InstitutionDTO[];
          }
          if (Array.isArray(payload.items)) {
            console.debug('[institutionService.getQueue] returned payload.items array, length=', payload.items.length);
            return payload.items as InstitutionDTO[];
          }

          const keys = Object.keys(payload || {});
          for (const k of keys) {
            if (Array.isArray((payload as any)[k])) {
              console.debug(`[institutionService.getQueue] returning first array found at payload.${k} (length=${(payload as any)[k].length})`);
              return (payload as any)[k] as InstitutionDTO[];
            }
          }

          console.warn('[institutionService.getQueue] unexpected payload shape for /institutions/queue, returning empty array', payload);
          return [];
        } catch (err: any) {
          lastErr = err;
          // If server error (5xx) we may retry; client errors (4xx) won't benefit from retry
          const status = err?.response?.status;
          const isServerError = typeof status === 'number' && status >= 500 && status < 600;
          attempt += 1;
          if (attempt > maxRetries || !isServerError) {
            // give up
            break;
          }
          const backoff = 300 * Math.pow(2, attempt - 1); // 300ms, 600ms, 1200ms...
          console.warn(`[institutionService.getQueue] attempt ${attempt} failed with server error ${status}, retrying after ${backoff}ms`);
          await new Promise((res) => setTimeout(res, backoff));
          continue;
        }
      }

      // If we reach here, all attempts failed
      throw lastErr ?? new Error('Unknown error fetching /institutions/queue');
    } catch (err: any) {
      // Log error details for easier debugging
      console.error('[institutionService.getQueue] error fetching queue after retries:', err);
      if (err?.response) {
        try {
          console.debug('[institutionService.getQueue] response status:', err.response.status, 'data:', err.response.data);
        } catch (e) { /* ignore */ }
      }

      if (swallow) {
        // Return empty list so UI can use fallback (listForUser) without noisy console errors
        return [];
      }

      // Rethrow so callers can handle retries/failures explicitly with full error
      // Include status/data for easier debugging upstream
      const errMsg = err?.response ? `HTTP ${err.response.status} - ${JSON.stringify(err.response.data)}` : (err?.message || String(err));
      const thrown = new Error(`[institutionService.getQueue] ${errMsg}`);
      (thrown as any).original = err;
      throw thrown;
    }
  },

  // Puxar instituição para análise (de AGUARDANDO_ANALISE → EM_ANALISE)
  puxarParaAnalise: async (id: string): Promise<InstitutionDTO> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/puxar-para-analise`);
    return response.data;
  },

  // OSC reprovada ajustou o cadastro e pede nova análise (REPROVADO → AGUARDANDO_ANALISE)
  reenviarAnalise: async (id: string): Promise<InstitutionDTO> => {
    const response = await api.post(`/institutions/${encodeURIComponent(id)}/reenviar-analise`, {});
    return response.data;
  },

  // Aprovar instituição (após análise de documentos)
  aprovar: async (id: string, observacoes?: string): Promise<InstitutionDTO> => {
    console.log('✅ institutionService.aprovar CHAMADO');
    console.log('   Institution ID:', id);
    console.log('   Observações:', observacoes);

    const response = await api.post(`/institutions/${encodeURIComponent(id)}/aprovar`, { observacoes });

    console.log('✅ institutionService.aprovar SUCESSO');
    return response.data;
  },

  // Reprovar instituição (após análise de documentos)
  reprovar: async (id: string, motivo: string): Promise<InstitutionDTO> => {
    console.log('❌ institutionService.reprovar CHAMADO');
    console.log('   Institution ID:', id);
    console.log('   Motivo:', motivo);

    const response = await api.post(`/institutions/${encodeURIComponent(id)}/reprovar`, { motivo });

    console.log('❌ institutionService.reprovar SUCESSO');
    return response.data;
  },

  // Serviço simples para recuperar dados mínimos da instituição (id, razaoSocial, nomeFantasia) com cache em memória para evitar múltiplas requests.
  getMinimos: async (institutionId: string) => {
    if (!institutionId) return null;
    if (cache[institutionId]) return cache[institutionId];

    try {
      const { data } = await api.get(`/institutions/minimos/${encodeURIComponent(institutionId)}`);
      const entry = { id: data.id, razaoSocial: data.razaoSocial, nomeFantasia: data.nomeFantasia };
      cache[institutionId] = entry;
      return entry;
    } catch (err: any) {
      // not found or other error
      console.warn('[institutionService] not found or error for', institutionId, err?.response?.status);
      cache[institutionId] = { id: institutionId };
      return cache[institutionId];
    }
  },
};

export default institutionService;

